import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from '../api/axios';

const getStrength = (pwd) => {
  if (!pwd) return { level: 0, label: '', color: '#eee' };
  let score = 0;
  if (pwd.length >= 6)               score++;
  if (pwd.length >= 10)              score++;
  if (/[A-Z]/.test(pwd))            score++;
  if (/[0-9]/.test(pwd))            score++;
  if (/[^A-Za-z0-9]/.test(pwd))    score++;
  if (score <= 1) return { level: 1, label: 'Weak',   color: '#e53935' };
  if (score <= 3) return { level: 2, label: 'Fair',   color: '#fb8c00' };
  if (score === 4) return { level: 3, label: 'Good',  color: '#43a047' };
  return              { level: 4, label: 'Strong', color: '#1e88e5' };
};

// ✅ Moved OUTSIDE the component so React doesn't recreate it on every render
const PasswordField = ({ name, label, showKey, placeholder, value, onChange, error, show, onToggleShow }) => (
  <div style={{ marginBottom: '20px' }}>
    <label style={{
      display: 'block', marginBottom: '6px',
      fontSize: '13px', fontWeight: 700, color: '#444'
    }}>
      {label}
    </label>
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          width: '100%', padding: '11px 44px 11px 14px',
          border: `1.5px solid ${error ? '#e53935' : '#e2e8f0'}`,
          borderRadius: '9px', fontSize: '14px',
          boxSizing: 'border-box', outline: 'none',
          backgroundColor: error ? '#fff5f5' : 'white',
          color: '#0f172a',
          fontFamily: "'Segoe UI', sans-serif",
          transition: 'border-color 0.2s',
        }}
        onFocus={e => e.target.style.borderColor = error ? '#e53935' : '#f1870e'}
        onBlur={e  => e.target.style.borderColor = error ? '#e53935' : '#e2e8f0'}
      />
      <button
        type="button"
        onClick={onToggleShow}
        style={{
          position: 'absolute', right: '12px', top: '50%',
          transform: 'translateY(-50%)',
          background: 'none', border: 'none',
          cursor: 'pointer', fontSize: '16px', color: '#aaa',
          padding: 0, lineHeight: 1
        }}
      >
        {show ? '🙈' : '👁️'}
      </button>
    </div>
    {error && (
      <p style={{ color: '#e53935', fontSize: '12px', margin: '5px 0 0' }}>
        ⚠ {error}
      </p>
    )}
  </div>
);

const ChangePassword = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    current_password: '',
    new_password:     '',
    confirm_password: ''
  });
  const [show, setShow]       = useState({ current: false, newp: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const toggleShow = (key) => {
    setShow(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const validate = () => {
    const e = {};
    if (!form.current_password)
      e.current_password = 'Current password is required';
    if (!form.new_password)
      e.new_password = 'New password is required';
    else if (form.new_password.length < 6)
      e.new_password = 'Password must be at least 6 characters';
    if (!form.confirm_password)
      e.confirm_password = 'Please confirm your new password';
    else if (form.new_password !== form.confirm_password)
      e.confirm_password = 'Passwords do not match';
    if (form.current_password && form.new_password && form.current_password === form.new_password)
      e.new_password = 'New password must differ from current password';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      await axios.put(`/users/${user.id}/change-password`, {
        current_password: form.current_password,
        new_password:     form.new_password
      });
      toast.success('Password changed successfully!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      navigate('/profile');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change password';
      if (msg.toLowerCase().includes('current') || msg.toLowerCase().includes('incorrect')) {
        setErrors({ current_password: 'Current password is incorrect' });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const strength = getStrength(form.new_password);

  return (
    <div style={{ padding: '32px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: '520px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <button
            onClick={() => navigate('/profile')}
            style={{
              background: 'none', border: '1px solid #e2e8f0',
              padding: '7px 16px', borderRadius: '9px',
              cursor: 'pointer', fontSize: '14px', color: '#555',
              fontFamily: "'Segoe UI', sans-serif"
            }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ color: '#f1870e', margin: 0, fontSize: '24px', fontWeight: 800 }}>
              Change Password
            </h1>
            <p style={{ color: '#94a3b8', margin: '2px 0 0', fontSize: '13px' }}>
              Keep your account secure with a strong password
            </p>
          </div>
        </div>

        {/* Security tip banner */}
        <div style={{
          backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
          borderRadius: '12px', padding: '14px 18px',
          marginBottom: '24px', display: 'flex', gap: '10px', alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '18px' }}>🔐</span>
          <div style={{ fontSize: '13px', color: '#c2410c', lineHeight: 1.6 }}>
            <b>Security tip:</b> Use at least 8 characters with a mix of uppercase letters, numbers, and symbols for a strong password.
          </div>
        </div>

        {/* Form Card */}
        <div style={{
          backgroundColor: 'white', borderRadius: '16px',
          boxShadow: '0 1px 8px rgba(0,0,0,0.08)', padding: '32px'
        }}>
          <form onSubmit={handleSubmit} noValidate>

            <PasswordField
              name="current_password"
              label="Current Password"
              showKey="current"
              placeholder="Enter your current password"
              value={form.current_password}
              onChange={handleChange}
              error={errors.current_password}
              show={show.current}
              onToggleShow={() => toggleShow('current')}
            />

            <div style={{ height: '1px', backgroundColor: '#f1f5f9', margin: '4px 0 20px' }} />

            <PasswordField
              name="new_password"
              label="New Password"
              showKey="newp"
              placeholder="Enter new password"
              value={form.new_password}
              onChange={handleChange}
              error={errors.new_password}
              show={show.newp}
              onToggleShow={() => toggleShow('newp')}
            />

            {/* Password strength bar */}
            {form.new_password && (
              <div style={{ marginTop: '-12px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                      flex: 1, height: '5px', borderRadius: '3px',
                      backgroundColor: i <= strength.level ? strength.color : '#e2e8f0',
                      transition: 'background 0.3s'
                    }} />
                  ))}
                </div>
                <span style={{ fontSize: '12px', color: strength.color, fontWeight: 600 }}>
                  {strength.label} password
                </span>
              </div>
            )}

            <PasswordField
              name="confirm_password"
              label="Confirm New Password"
              showKey="confirm"
              placeholder="Re-enter your new password"
              value={form.confirm_password}
              onChange={handleChange}
              error={errors.confirm_password}
              show={show.confirm}
              onToggleShow={() => toggleShow('confirm')}
            />

            {/* Match indicator */}
            {form.confirm_password && form.new_password && (
              <div style={{
                marginTop: '-12px', marginBottom: '20px',
                fontSize: '12px', fontWeight: 600,
                color: form.new_password === form.confirm_password ? '#43a047' : '#e53935'
              }}>
                {form.new_password === form.confirm_password
                  ? '✅ Passwords match'
                  : '❌ Passwords do not match'}
              </div>
            )}

            {/* Requirements checklist */}
            <div style={{
              backgroundColor: '#f8fafc', borderRadius: '10px',
              padding: '14px 18px', marginBottom: '24px',
              border: '1px solid #f1f5f9'
            }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', marginBottom: '10px', letterSpacing: '0.5px' }}>
                PASSWORD REQUIREMENTS
              </div>
              {[
                { label: 'At least 6 characters',          met: form.new_password.length >= 6 },
                { label: 'At least one uppercase letter',   met: /[A-Z]/.test(form.new_password) },
                { label: 'At least one number',             met: /[0-9]/.test(form.new_password) },
                { label: 'At least one special character',  met: /[^A-Za-z0-9]/.test(form.new_password) },
              ].map(({ label, met }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontSize: '13px', color: met ? '#16a34a' : '#94a3b8',
                  marginBottom: '6px', fontWeight: met ? 600 : 400
                }}>
                  <span style={{ fontSize: '11px' }}>{met ? '✅' : '⬜'}</span> {label}
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1, padding: '13px',
                  background: 'linear-gradient(135deg, #f1870e, #fb923c)',
                  color: 'white', border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  boxShadow: '0 4px 12px rgba(241,135,14,0.35)',
                  fontFamily: "'Segoe UI', sans-serif"
                }}
              >
                {loading ? '⏳ Updating...' : '🔒 Update Password'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                style={{
                  padding: '13px 24px',
                  backgroundColor: '#f8fafc', color: '#64748b',
                  border: '1px solid #e2e8f0', borderRadius: '10px',
                  fontSize: '15px', cursor: 'pointer',
                  fontFamily: "'Segoe UI', sans-serif"
                }}
              >
                Cancel
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;